import {
  Background,
  Controls,
  MiniMap,
  ReactFlow,
  addEdge,
  applyEdgeChanges,
  applyNodeChanges,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";

import { Box, Button, Flex, Heading, Text, VStack } from "@chakra-ui/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import NodePropertiesPanel from "./NodePropertiesPanel";
import WorkflowNode from "./WorkflowNode";
import { CATEGORIES, NODE_TYPES, defaultNodeData, nodeTypesByCategory } from "./nodeTypes";

const NODE_DEFAULT_WIDTH = 240;
const NODE_DEFAULT_HEIGHT = 90;

// React Flow expects node types to be passed by reference; create once.
const NODE_RENDERERS = { workflowNode: WorkflowNode };

// ─── Conversion: backend definition <-> React Flow internal state ────────────

function defToFlow(definition, handlers) {
  const rfNodes = (definition?.nodes || []).map((n, idx) => ({
    id: n.id,
    type: "workflowNode",
    position: n.position || { x: 80 + (idx % 3) * 280, y: 60 + Math.floor(idx / 3) * 180 },
    data: { ...n.data, type: n.type, _onDelete: handlers.onDelete, _onSelect: handlers.onSelect },
  }));
  const rfEdges = (definition?.edges || []).map((e, idx) => ({
    id: e.id || `e-${idx}-${e.source}-${e.target}-${e.source_handle || ""}`,
    source: e.source,
    target: e.target,
    sourceHandle: e.source_handle || e.sourceHandle,
    type: "default",
    animated: false,
  }));
  return { rfNodes, rfEdges };
}

function flowToDef(rfNodes, rfEdges) {
  const nodes = rfNodes.map((n) => {
    const { _onDelete, _onSelect, type: nodeType, ...rest } = n.data || {};
    return {
      id: n.id,
      type: nodeType,
      position: n.position,
      data: rest,
    };
  });
  const edges = rfEdges.map((e) => ({
    source: e.source,
    target: e.target,
    source_handle: e.sourceHandle || undefined,
  }));
  return { nodes, edges };
}

// ─── Palette (left rail) ─────────────────────────────────────────────────────

function Palette({ onAdd }) {
  const byCategory = useMemo(() => nodeTypesByCategory(), []);
  return (
    <Box
      w="220px"
      bg="gray.50"
      borderRight="1px solid"
      borderColor="gray.200"
      h="100%"
      overflowY="auto"
      p={3}
    >
      <Heading size="xs" mb={3} color="gray.600">
        Add a node
      </Heading>
      {CATEGORIES.map((cat) => {
        const items = byCategory[cat] || [];
        if (items.length === 0) return null;
        return (
          <Box key={cat} mb={4}>
            <Text fontSize="xs" textTransform="uppercase" color="gray.500" mb={1}>
              {cat}
            </Text>
            <VStack align="stretch" spacing={1}>
              {items.map((item) => {
                const Icon = item.icon;
                return (
                  <Button
                    key={item.key}
                    size="sm"
                    variant="ghost"
                    justifyContent="flex-start"
                    leftIcon={
                      <Box
                        w="20px"
                        h="20px"
                        bg={item.color}
                        color="white"
                        borderRadius="sm"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                      >
                        <Icon size={12} />
                      </Box>
                    }
                    onClick={() => onAdd(item.key)}
                  >
                    {item.label}
                  </Button>
                );
              })}
            </VStack>
          </Box>
        );
      })}
    </Box>
  );
}

// ─── Main canvas component ───────────────────────────────────────────────────

export default function WorkflowCanvas({ definition, onChange }) {
  const [rfNodes, setRfNodes] = useState([]);
  const [rfEdges, setRfEdges] = useState([]);
  const [selectedNodeId, setSelectedNodeId] = useState(null);
  const idCounter = useRef(0);

  // Initial load from prop. Re-run if definition reference changes.
  const handlers = useMemo(
    () => ({
      onDelete: (id) => {
        setRfNodes((nds) => nds.filter((n) => n.id !== id));
        setRfEdges((eds) => eds.filter((e) => e.source !== id && e.target !== id));
        setSelectedNodeId((cur) => (cur === id ? null : cur));
      },
      onSelect: (id) => setSelectedNodeId(id),
    }),
    []
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const { rfNodes: ns, rfEdges: es } = defToFlow(definition, handlers);
    setRfNodes(ns);
    setRfEdges(es);
    // initialize id counter higher than any existing numeric suffix
    let max = 0;
    for (const n of ns) {
      const m = /^n(\d+)$/.exec(n.id);
      if (m) max = Math.max(max, parseInt(m[1], 10));
    }
    idCounter.current = max;
  }, [definition]);

  // Whenever local state changes, emit converted definition upstream.
  useEffect(() => {
    if (!onChange) return;
    onChange(flowToDef(rfNodes, rfEdges));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rfNodes, rfEdges]);

  const onNodesChange = useCallback((changes) => {
    setRfNodes((nds) => applyNodeChanges(changes, nds));
  }, []);
  const onEdgesChange = useCallback((changes) => {
    setRfEdges((eds) => applyEdgeChanges(changes, eds));
  }, []);
  const onConnect = useCallback((conn) => {
    setRfEdges((eds) =>
      addEdge(
        { ...conn, id: `e-${conn.source}-${conn.target}-${conn.sourceHandle || ""}-${Date.now()}` },
        eds
      )
    );
  }, []);

  const addNode = useCallback(
    (typeKey) => {
      idCounter.current += 1;
      const id = `n${idCounter.current}`;
      const meta = NODE_TYPES[typeKey];
      const x = 200 + Math.random() * 200;
      const y = 200 + Math.random() * 200;
      setRfNodes((nds) => [
        ...nds,
        {
          id,
          type: "workflowNode",
          position: { x, y },
          data: {
            type: typeKey,
            ...defaultNodeData(typeKey),
            _onDelete: handlers.onDelete,
            _onSelect: handlers.onSelect,
          },
        },
      ]);
      setSelectedNodeId(id);
    },
    [handlers]
  );

  const selectedNode = rfNodes.find((n) => n.id === selectedNodeId);

  const updateSelected = useCallback((newNode) => {
    setRfNodes((nds) =>
      nds.map((n) =>
        n.id === newNode.id
          ? { ...n, data: { ...newNode.data, _onDelete: handlers.onDelete, _onSelect: handlers.onSelect } }
          : n
      )
    );
  }, [handlers]);

  return (
    <Flex h="100%" w="100%" overflow="hidden">
      <Palette onAdd={addNode} />
      <Box flex="1" h="100%" position="relative" bg="gray.50">
        <ReactFlow
          nodes={rfNodes}
          edges={rfEdges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          nodeTypes={NODE_RENDERERS}
          fitView
          fitViewOptions={{ padding: 0.2 }}
          deleteKeyCode={["Backspace", "Delete"]}
          onPaneClick={() => setSelectedNodeId(null)}
        >
          <Background />
          <Controls />
          <MiniMap pannable zoomable />
        </ReactFlow>
      </Box>
      {selectedNode ? (
        <Box w="340px" h="100%" flexShrink={0}>
          <NodePropertiesPanel
            node={selectedNode}
            onChange={updateSelected}
            onClose={() => setSelectedNodeId(null)}
          />
        </Box>
      ) : null}
    </Flex>
  );
}
